import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, X, Check, Search, ChevronDown, Loader2, History, Activity } from "lucide-react";
import axiosInstance from "../../../API/interceptor";
import Swal from "sweetalert2";
import { CONFIRM_CLASSES, successToast, errorToast } from "../swalTheme";
import PaginationBar from "../components/PaginationBar";

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 transition-all";
const PAGE_SIZE = 10;
const HEADER_GRADIENT = "bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]";
const PILL_CLASSES = "bg-[#6c5ffc] text-white";

export default function WorkspaceManagementTab() {
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [ownerSearch, setOwnerSearch] = useState("");
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(PAGE_SIZE);
  const [modal,     setModal]     = useState(null);

  // owners: { product_code: [{ id: "staffid", text: "Name (email)" }] }
  const [owners,        setOwners]        = useState({});
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownerPop,      setOwnerPop]      = useState(null); // { productCode, options, q }

  // ── Load all products + owners in parallel ───────────────────
  const loadAll = useCallback(() => {
    setLoading(true);
    axiosInstance.get("/task_assignment/get_products")
      .then(res => {
        const prods = res.data.products || [];
        setProducts(prods);
        // Fetch all product details in parallel for owner names
        setOwnersLoading(true);
        Promise.all(
          prods.map(p =>
            axiosInstance.get(`/task_assignment/get_product/${p.product_code}`)
              .then(r => {
                const prod = r.data.product;
                // name_email contains "Name (email)" strings
                // product_owner contains staff_id strings
                const ownerIds   = prod?.product_owner  || [];
                const nameEmails = prod?.name_email      || [];
                return {
                  code: p.product_code,
                  owners: ownerIds.map((id, i) => ({
                    id:   String(id),
                    text: nameEmails[i] || String(id),
                  })),
                };
              })
              .catch(() => ({ code: p.product_code, owners: [] }))
          )
        ).then(results => {
          const map = {};
          results.forEach(r => { map[r.code] = r.owners; });
          setOwners(map);
        }).finally(() => setOwnersLoading(false));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Add ──────────────────────────────────────────────────────
  function submitAdd(name) {
    axiosInstance.post("/task_assignment/add_product", { product_name: name.trim() })
      .then(res => {
        setModal(null);
        successToast.fire({ icon: "success", title: res.data.message });
        loadAll();
      })
      .catch(err => errorToast.fire({ icon: "error", title: err.response?.data?.message || "Error adding" }));
  }

  // ── Edit ─────────────────────────────────────────────────────
  function openEdit(productCode) {
    axiosInstance.get(`/task_assignment/get_product/${productCode}`)
      .then(res => setModal({ type: "edit", product_code: res.data.product.product_code, name: res.data.product.name }))
      .catch(() => errorToast.fire({ icon: "error", title: "Error loading product" }));
  }

  // ── Activity log ─────────────────────────────────────────────
  function openLog(productCode) {
    setModal({ type: "log", loading: true, name: "", activity_log: [] });
    axiosInstance.get(`/task_assignment/get_product/${productCode}`)
      .then(res => setModal({
        type: "log",
        loading: false,
        name: res.data.product.name,
        activity_log: res.data.product.activity_log || [],
      }))
      .catch(() => {
        errorToast.fire({ icon: "error", title: "Error loading activity log" });
        setModal(null);
      });
  }

  function submitEdit(product_code, name) {
    axiosInstance.post(`/task_assignment/edit_product/${product_code}`, { product_name: name.trim() })
      .then(res => {
        setModal(null);
        successToast.fire({ icon: "success", title: res.data.message });
        loadAll();
      })
      .catch(err => errorToast.fire({ icon: "error", title: err.response?.data?.message || "Error updating" }));
  }

  // ── Delete ───────────────────────────────────────────────────
  function deleteProduct(productCode) {
    Swal.fire({
      title: "Delete workspace?",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: CONFIRM_CLASSES,
    }).then(r => {
        if (!r.isConfirmed) return;
        axiosInstance.post(`/task_assignment/delete_product/${productCode}`)
          .then(res => {
            if (res.data.can_delete) {
              successToast.fire({ icon: "success", title: "Deleted!" });
              loadAll();
            } else {
              errorToast.fire({ icon: "error", title: res.data.message });
            }
          })
          .catch(() => errorToast.fire({ icon: "error", title: "Error deleting" }));
      });
  }

  // ── Owner popup ──────────────────────────────────────────────
  const searchTimerRef = useRef(null);
  const searchAbortRef = useRef(null);

  function runUserSearch(q, productCode) {
    searchAbortRef.current?.abort();
    searchAbortRef.current = new AbortController();
    axiosInstance.get("/task_assignment/get_users_all", { params: { q }, signal: searchAbortRef.current.signal })
      .then(res => {
        setOwnerPop(prev =>
          prev && prev.productCode === productCode
            ? { ...prev, options: (res.data || []).map(u => ({ id: String(u.staffid), text: u.display_name || u.email || String(u.staffid) })) }
            : prev
        );
      })
      .catch(e => { if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") {} });
  }

  function openOwnerPop(productCode) {
    setOwnerPop({ productCode, options: [], q: "" });
    runUserSearch("", productCode); // initial load — a single deliberate action, not a keystroke
  }

  // Debounced — waits for typing to pause and cancels the previous in-flight request
  function fetchUsers(q, productCode) {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => runUserSearch(q, productCode), 300);
  }

  // ── Remove owner (x button) ──────────────────────────────────
  function removeOwner(productCode, ownerId) {
    const current = owners[productCode] || [];
    const target  = current.find(o => o.id === ownerId);

    Swal.fire({
      title: "Remove Product Owner?",
      text: target ? `Do you want to remove ${target.text}?` : "Do you want to remove this owner?",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: CONFIRM_CLASSES,
    }).then(r => {
      if (!r.isConfirmed) return;
      const updated = current.filter(o => o.id !== ownerId);
      setOwners(prev => ({ ...prev, [productCode]: updated }));
      // API: user_id = list of remaining staff_ids
      axiosInstance.post("/task_assignment/update_product_owner", {
        product_code: productCode,
        user_id: updated.map(o => o.id),
      }).then(() => {
        successToast.fire({ icon: "success", title: "Owner removed" });
      }).catch(() => loadAll()); // rollback on error
    });
  }

  // ── Add owner from popup ─────────────────────────────────────
  function addOwner(productCode, user) {
    const current = owners[productCode] || [];
    if (current.find(o => o.id === user.id)) return; // already added
    const updated = [...current, user];
    setOwners(prev => ({ ...prev, [productCode]: updated }));
    axiosInstance.post("/task_assignment/update_product_owner", {
      product_code: productCode,
      user_id: updated.map(o => o.id),
    }).then(() => {
      successToast.fire({ icon: "success", title: "Owner added" });
    }).catch(() => loadAll());
    setOwnerPop(null);
  }

  // ── Pagination + search ──────────────────────────────────────
  const filtered = products.filter(p => {
    const matchesWorkspace =
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      String(p.product_code).includes(search);

    const ownerQuery = ownerSearch.trim().toLowerCase();
    const matchesOwner = !ownerQuery ||
      (owners[p.product_code] || []).some(o => o.text.toLowerCase().includes(ownerQuery));

    return matchesWorkspace && matchesOwner;
  });
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

  function handleSearch(val) { setSearch(val); setPage(1); }
  function handleOwnerSearch(val) { setOwnerSearch(val); setPage(1); }

  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            Search:
            <div className="relative">
              <input
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 w-[200px]"
                placeholder=""
                value={search}
                onChange={e => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            Search by Owner:
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 w-[220px]"
                placeholder="Employee name..."
                value={ownerSearch}
                onChange={e => handleOwnerSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <button
          onClick={() => setModal({ type: "add", name: "" })}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer border-none hover:opacity-90 transition-all ${HEADER_GRADIENT}`}
        >
          <Plus size={14} /> Add New Workspace
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-gray-100">
              <th className="px-4 py-3 text-left font-semibold">Index ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Product Name ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Product Code ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Product Owner</th>
              <th className="px-4 py-3 text-left font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(5)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-gray-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginated.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No workspaces found</td></tr>
            ) : paginated.map((prod, idx) => (
              <tr key={prod.product_code} className="bg-white hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 text-slate-400 text-xs">{(page - 1) * pageSize + idx + 1}</td>
                <td className="px-4 py-3 font-semibold text-slate-700">{prod.name}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs font-mono">{prod.product_code}</span>
                </td>
                <td className="px-4 py-3">
                  <OwnerCell
                    productCode={prod.product_code}
                    owners={owners[prod.product_code] || []}
                    loadingOwners={ownersLoading}
                    isOpen={ownerPop?.productCode === prod.product_code}
                    pop={ownerPop}
                    onOpen={() => openOwnerPop(prod.product_code)}
                    onClose={() => setOwnerPop(null)}
                    onSearch={q => { setOwnerPop(prev => prev ? { ...prev, q } : null); fetchUsers(q, prod.product_code); }}
                    onRemove={ownerId => removeOwner(prod.product_code, ownerId)}
                    onAdd={user => addOwner(prod.product_code, user)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(prod.product_code)}
                      className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 transition-colors" title="Edit">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => openLog(prod.product_code)}
                      className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" title="Activity Log">
                      <History size={13} />
                    </button>
                    <button onClick={() => deleteProduct(prod.product_code)}
                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: showing X to Y of Z entries + pagination */}
      <PaginationBar
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        totalRecords={filtered.length}
      />

      {/* Close owner popup overlay */}
      {ownerPop && <div className="fixed inset-0 z-30" onClick={() => setOwnerPop(null)} />}

      {/* Add / Edit Modal */}
      {modal && (modal.type === "add" || modal.type === "edit") && (
        <WorkspaceModal
          modal={modal}
          onChange={setModal}
          onSubmit={modal.type === "add" ? submitAdd : submitEdit}
          onClose={() => setModal(null)}
        />
      )}

      {/* Activity Log Modal */}
      {modal && modal.type === "log" && (
        <ActivityLogModal modal={modal} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ── Owner cell — fixed-position dropdown to avoid table clipping ──
function OwnerCell({ productCode, owners, loadingOwners, isOpen, pop, onOpen, onClose, onSearch, onRemove, onAdd }) {
  const triggerRef = useRef(null);
  const [rect, setRect] = useState(null);

  function handleOpen() {
    onOpen();
  }

  // Track the trigger's position while open — position:fixed doesn't move on
  // its own when the page/table scrolls, so recompute on scroll/resize or the
  // dropdown stays stuck where it first opened instead of following the row.
  useEffect(() => {
    if (!isOpen) { setRect(null); return; }

    const updateRect = () => {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    };
    updateRect();

    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen]);

  return (
    <div>
      {/* Trigger area */}
      <div
        ref={triggerRef}
        className="flex flex-wrap gap-1 items-center min-h-8 cursor-pointer"
        onClick={loadingOwners ? undefined : (isOpen ? onClose : handleOpen)}
      >
        {loadingOwners ? (
          <span className="text-slate-400 text-xs flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Loading...
          </span>
        ) : owners.length === 0 ? (
          <span className="text-slate-400 text-xs hover:text-indigo-500 transition-colors flex items-center gap-1">
            Select a Product Owner <ChevronDown size={11} />
          </span>
        ) : (
          <>
            {owners.map(o => (
              <span
                key={o.id}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${PILL_CLASSES}`}
                onClick={e => e.stopPropagation()}
              >
                <span className="cursor-pointer hover:opacity-70 font-bold" onClick={() => onRemove(o.id)}>×</span>
                {o.text}
              </span>
            ))}
            <ChevronDown size={11} className="text-slate-400 ml-1" />
          </>
        )}
      </div>

      {/* Fixed-position dropdown — won't be clipped by table */}
      {isOpen && rect && (
        <div
          className="bg-white border border-gray-200 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-80 fixed z-[9999]"
          style={{ top: rect.bottom + 4, left: rect.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="Search users..."
                value={pop?.q || ""}
                onChange={e => onSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {(pop?.options || []).length === 0 ? (
              <div className="p-3 text-xs text-slate-400 text-center">Start typing to search users</div>
            ) : pop.options.map(u => {
              const alreadyAdded = owners.some(o => o.id === u.id);
              return (
                <div
                  key={u.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm ${alreadyAdded ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"}`}
                  onClick={() => !alreadyAdded && onAdd(u)}
                >
                  <span className="text-slate-700 flex-1">{u.text}</span>
                  {alreadyAdded && <span className="text-xs text-indigo-400">Added</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity Log Offcanvas ────────────────────────────────────────
const LOG_PAGE_SIZE = 10;

function ActivityLogModal({ modal, onClose }) {
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(LOG_PAGE_SIZE);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const logs = [...(modal.activity_log || [])].reverse();
  const pageLogs = logs.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black/35 z-50" />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-[50rem] bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.15)] z-50 flex flex-col translate-x-0 transition-transform duration-300 ease-in-out">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h5 className="font-semibold text-slate-700">
            Activity Log{modal.name ? ` — ${modal.name}` : ""}
          </h5>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1 flex flex-col gap-3">
          {modal.loading ? (
            <div className="text-center py-6 text-slate-400 text-sm flex items-center justify-center gap-1.5">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-sm">No activity yet</div>
          ) : pageLogs.map((log, i) => (
            <div key={i} className="flex gap-2.5 items-start">
              <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                <Activity size={12} className="text-indigo-500" />
              </div>
              <div className="flex-1">
                {log.log_type && (
                  <span className="bg-slate-100 text-slate-600 text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-1 inline-block">
                    {log.log_type}
                  </span>
                )}
                <p className="text-sm text-slate-700 my-0.5 leading-snug">{log.details || ""}</p>
                <p className="text-xs text-slate-400 m-0">
                  {log.log_user_name || ""}{log.date_time ? ` · ${log.date_time}` : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
        {!modal.loading && logs.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100">
            <PaginationBar
              page={page}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              totalRecords={logs.length}
              pageSizeOptions={[10, 25, 50]}
            />
          </div>
        )}
      </div>
    </>
  );
}

// ── Add / Edit Modal ─────────────────────────────────────────────
function WorkspaceModal({ modal, onChange, onSubmit, onClose }) {
  const isEdit = modal.type === "edit";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35">
      <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-sm mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h5 className="font-semibold text-slate-700">{isEdit ? "Edit Workspace" : "Add New Product"}</h5>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="px-6 py-5">
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Product Name</label>
          <input
            className={inputCls}
            placeholder="Product Name"
            value={modal.name}
            onChange={e => onChange(m => ({ ...m, name: e.target.value }))}
            autoFocus
          />
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => isEdit ? onSubmit(modal.product_code, modal.name) : onSubmit(modal.name)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg border-none cursor-pointer hover:opacity-90 ${HEADER_GRADIENT}`}
          >
            <Check size={13} /> {isEdit ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
}